import React from 'react';
import { render } from 'react-dom';
import { BrowserRouter as Router } from 'react-router-dom';
import { Provider as StoreProvider } from 'react-redux';
import { AppDomain } from '@pegasus/config';
import { TranslateProvider } from '@pegasus/translation';
import { activateScripts } from 'zalando-consent-api';
import { subscribeToConsentChange as subscribeToSentryConsentChange } from '@pegasus/sentry';

import { messages } from 'src/locales/at';
import { loadConfig } from 'src/config';
import { ConfigProvider } from 'src/client/hoc/config';
import { buildRoutes } from 'src/client/routes';
import { CoreAppsLayout, CoreWebLayout } from 'src/client/layouts/Core';
import { isSentryEnabled } from 'src/lib/sentry';

import { ColorMode, LUXProvider, LUXV2Theme } from '@lux/core';
import { configureStore } from './components/shared/redux';
import { isWebView, getWebviewTheme } from './helpers';
import { GlobalStyle } from './components/shared/styles/global-styles';
import { CustomerProvider } from './hooks/useCustomer';
import { exposeSharedDependencies } from './lib/sharedDependencies';

if (window.__webpack_public_path__) {
    __webpack_public_path__ = window.__webpack_public_path__;
}

const appdomainId = ((typeof window !== 'undefined' && window.APPDOMAIN_ID) ||
    process.env.APPDOMAIN ||
    '17') as AppDomain;

const config = loadConfig(appdomainId);
const translations = messages(config.appdomain);

let initialState: Record<string, unknown> = {};
try {
    if (typeof window !== 'undefined' && window.__INITIAL_STATE__) {
        initialState = { ...initialState, ...window.__INITIAL_STATE__ };
        delete initialState.date;
        delete initialState.processingTime;
        delete window.__INITIAL_STATE__;
    }
} catch (e) {
    // eslint-disable-next-line no-console
    console.error('catch', e);
}

const store = configureStore(initialState);

const renderApp = (container: HTMLElement) => {
    const cms = container.getAttribute('cms');
    const cmsContent = JSON.parse(decodeURIComponent(String(cms)));

    const coreLayoutComponent = isWebView() ? CoreAppsLayout : CoreWebLayout;

    const colorMode = getWebviewTheme() === 'dark' ? ColorMode.DARK : ColorMode.LIGHT;

    render(
        <StoreProvider store={store}>
            <ConfigProvider config={config}>
                <TranslateProvider translations={translations}>
                    {/* If additional state similar to this is needed later on, this can be changed to a StateProvider which aggregates these values */}
                    <CustomerProvider>
                        <LUXProvider initialColorMode={colorMode} LUXTheme={LUXV2Theme}>
                            <GlobalStyle />
                            <Router>{buildRoutes({ coreLayoutComponent, cmsContent })}</Router>
                        </LUXProvider>
                    </CustomerProvider>
                </TranslateProvider>
            </ConfigProvider>
        </StoreProvider>,
        container,
    );
};

const startApp = () => {
    /* eslint-disable no-console */
    // add third-party consents scripts based on consent
    activateScripts()
        .then(
            () => console.log('Third party scripts with consent are activated'),
            () => console.error('An error has occurred activatiing scripts'),
        )
        .finally(() => {
            if (isSentryEnabled(config)) subscribeToSentryConsentChange();
        });

    // exposing base libraries to be consumed by the SDK
    exposeSharedDependencies();

    const root = document.getElementById('react-container');

    if (!root) {
        throw new TypeError('Cannot find root element');
    }

    if (__DEV__) {
        /* eslint-disable-next-line global-require */
        const RedBox = require('redbox-react');
        try {
            renderApp(root);
        } catch (e) {
            render(<RedBox error={e} />, root);
        }
    } else {
        renderApp(root);
    }
};

startApp();
